import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import Popup from "../../Popup/Popup";
import Button from "../../Button/Button";
import ToggleSlider from "../../ToggleSlider/ToggleSlider";
import { AreaDisplayInfo } from "../../../../Types/AreaDisplayInfo/AreaDisplayInfo";
import { downloadBlob } from "../../../../Utils/Export/downloadBlob";
import { openBlobInNewTab } from "../../../../Utils/Export/openInNewTab";
import { fetchAreaExportData } from "../../../../Utils/Export/fetchAreaExportData";
import { getExporter } from "../../../../Utils/Export/exporters";
import {
  DEFAULT_EXPORT_FIELD_VISIBILITY,
  EXPORT_TOGGLES,
  ExportColumnKey,
  ExportFieldVisibility,
  ExportFormat,
  fullExportFieldVisibility,
} from "../../../../Utils/Export/types";
import ExportFormatChoiceGrid from "../../ExportFormatChoice/ExportFormatChoiceGrid";
import {
  EXPORT_FORMAT_ORDER,
  getExportFormatUnavailableReason,
} from "../../ExportFormatChoice/exportFormatDisplay";
import { EXPORT_POPUP_SHELL_STYLE } from "../../../../Utils/Export/exportPopupShellStyle";
import styles from "./BulkExportWizard.module.css";

export type BulkExportWizardProps = {
  isVisible: boolean;
  onClose: () => void;
  /** Full list page rows; exportable ones are filtered internally. */
  areas: AreaDisplayInfo[];
  /** Product segment: Mesma-{productName}-Export-date */
  productName?: string;
  /** Pre-select these area ids when the wizard opens (e.g. per-card export). */
  initialSelectedIds?: string[];
};

function defaultBaseFilename(productName: string): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `Mesma-${productName}-Export-${d.getFullYear()}-${pad(
    d.getMonth() + 1
  )}-${pad(d.getDate())}`;
}

const BulkExportWizard: React.FC<BulkExportWizardProps> = ({
  isVisible,
  onClose,
  areas,
  productName = "Mesma",
  initialSelectedIds,
}) => {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [fieldVisibility, setFieldVisibility] = useState<ExportFieldVisibility>(
    () => ({ ...DEFAULT_EXPORT_FIELD_VISIBILITY })
  );
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const exportable = useMemo(
    () =>
      areas
        .filter((a) => a.canExport)
        .slice()
        .sort((a, b) =>
          (a.title || "Untitled").localeCompare(b.title || "Untitled", undefined, {
            sensitivity: "base",
          })
        ),
    [areas]
  );

  const allExportableSelected = useMemo(
    () =>
      exportable.length > 0 &&
      exportable.every((a) => selectedIds.has(a.id)),
    [exportable, selectedIds]
  );

  const toggleSelectAllExportable = useCallback(() => {
    if (exportable.length === 0) return;
    if (allExportableSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(exportable.map((a) => a.id)));
    }
  }, [allExportableSelected, exportable]);

  useEffect(() => {
    if (!isVisible) {
      setStep(1);
      setSelectedIds(new Set());
      setFieldVisibility({ ...DEFAULT_EXPORT_FIELD_VISIBILITY });
      setError(null);
      setRunning(false);
      return;
    }

    if (initialSelectedIds?.length) {
      const exportableIds = new Set(exportable.map((a) => a.id));
      const preselected = initialSelectedIds.filter((id) => exportableIds.has(id));
      setSelectedIds(new Set(preselected));
    } else {
      setSelectedIds(new Set());
    }
  }, [isVisible, initialSelectedIds, exportable]);

  const displayInfoById = useMemo(() => {
    const m: Record<string, AreaDisplayInfo> = {};
    for (const a of areas) {
      m[a.id] = a;
    }
    return m;
  }, [areas]);

  const toggleArea = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const setColumnOn = useCallback((key: ExportColumnKey, on: boolean) => {
    setFieldVisibility((prev) => ({ ...prev, [key]: on }));
  }, []);

  const activeColumnCount = useMemo(
    () => EXPORT_TOGGLES.filter((k) => fieldVisibility[k]).length,
    [fieldVisibility]
  );

  const allColumnsSelected = useMemo(
    () => EXPORT_TOGGLES.every((k) => fieldVisibility[k]),
    [fieldVisibility]
  );

  const toggleSelectAllColumns = useCallback(() => {
    setFieldVisibility((prev) => {
      const allOn = EXPORT_TOGGLES.every((k) => prev[k]);
      const next = { ...prev };
      for (const k of EXPORT_TOGGLES) {
        next[k] = !allOn;
      }
      return next;
    });
  }, []);

  const runExport = useCallback(
    async (format: ExportFormat) => {
      if (getExportFormatUnavailableReason(format)) {
        return;
      }

      const ids = Array.from(selectedIds);
      if (ids.length === 0) {
        toast.error("No areas selected to export.");
        return;
      }
      if (activeColumnCount === 0) {
        setError("Select at least one field to include in the export.");
        return;
      }

      const entry = getExporter(format);
      if (!entry) {
        toast.error("This export format is not available.");
        return;
      }

      setRunning(true);
      setError(null);
      try {
        const vis = fullExportFieldVisibility(fieldVisibility);
        const data = await fetchAreaExportData(ids, { displayInfoById });
        const base = defaultBaseFilename(productName);
        const blob = await entry.exporter(data, base, {
          fieldVisibility: vis,
        });
        downloadBlob(blob, `${base}.${entry.meta.extension}`);
        toast.success(
          ids.length > 1
            ? `Exported ${ids.length} areas.`
            : "Export completed."
        );
        onClose();
      } catch (e) {
        console.error(e);
        toast.error("Export failed. Please try again.");
      } finally {
        setRunning(false);
      }
    },
    [
      activeColumnCount,
      displayInfoById,
      fieldVisibility,
      onClose,
      productName,
      selectedIds,
    ]
  );

  const runHtmlExport = useCallback(async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) {
      toast.error("No areas selected to export.");
      return;
    }
    if (activeColumnCount === 0) {
      setError("Select at least one field to include in the export.");
      return;
    }

    const entry = getExporter("html");
    if (!entry) {
      toast.error("This export format is not available.");
      return;
    }

    setRunning(true);
    setError(null);
    try {
      const vis = fullExportFieldVisibility(fieldVisibility);
      const data = await fetchAreaExportData(ids, { displayInfoById });
      const base = defaultBaseFilename(productName);
      const blob = await entry.exporter(data, base, {
        fieldVisibility: vis,
      });
      openBlobInNewTab(blob);
      toast.success(
        ids.length > 1
          ? `Opened report for ${ids.length} areas.`
          : "Report opened in a new tab."
      );
      onClose();
    } catch (e) {
      console.error(e);
      toast.error("Export failed. Please try again.");
    } finally {
      setRunning(false);
    }
  }, [
    activeColumnCount,
    displayInfoById,
    fieldVisibility,
    onClose,
    productName,
    selectedIds,
  ]);

  const canNextStep1 = selectedIds.size > 0;
  const canNextStep2 = activeColumnCount > 0;

  return (
    <Popup
      isVisible={isVisible}
      onClose={onClose}
      style={EXPORT_POPUP_SHELL_STYLE}
    >
      <div className={styles.content}>
        <h3 className={styles.contentTitle}>Export data</h3>

        <div className={styles.stepMain}>
          {step === 1 ? (
            <>
              <p className={styles.stepHint}>
                Select one or more diagnostics to include in the export.
              </p>
              <div className={styles.listToolbar}>
                <Button
                  size="Small"
                  colour="#FFF"
                  textColour="#000"
                  border="rgba(0,0,0,0.2)"
                  disabled={running || exportable.length === 0}
                  clickHandler={toggleSelectAllExportable}
                >
                  {allExportableSelected ? "Deselect all" : "Select all"}
                </Button>
              </div>
              <div className={styles.list} role="group" aria-label="Diagnostics">
                {exportable.length === 0 ? (
                  <p className={styles.stepHint}>Nothing available to export.</p>
                ) : (
                  exportable.map((a) => (
                    <label key={a.id} className={styles.areaRow}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(a.id)}
                        onChange={() => toggleArea(a.id)}
                      />
                      <span>{a.title || "Untitled"}</span>
                    </label>
                  ))
                )}
              </div>
            </>
          ) : null}

          {step === 2 ? (
            <>
              <p className={styles.stepHint}>
                Choose which columns and blocks to include. Disabled items are
                omitted from the export.
              </p>
              <div className={styles.listToolbar}>
                <Button
                  size="Small"
                  colour="#FFF"
                  textColour="#000"
                  border="rgba(0,0,0,0.2)"
                  disabled={running}
                  clickHandler={toggleSelectAllColumns}
                >
                  {allColumnsSelected ? "Deselect all" : "Select all"}
                </Button>
              </div>
              <div className={styles.toggleList}>
                {EXPORT_TOGGLES.map((key) => (
                  <div key={key} className={styles.toggleRow}>
                    <span className={styles.toggleLabel}>{key}</span>
                    <ToggleSlider
                      isOn={fieldVisibility[key]}
                      onToggle={(next) => setColumnOn(key, next)}
                      disableConfirm
                      size="small"
                    />
                  </div>
                ))}
              </div>
            </>
          ) : null}

          {step === 3 ? (
            <>
              <p className={styles.stepHint}>
                Choose a file format. Your column choices from the previous step
                apply to Excel, Word, and the HTML report.
              </p>
              <div className={styles.formatRow}>
                <ExportFormatChoiceGrid
                  variant="tiles"
                  formats={EXPORT_FORMAT_ORDER}
                  disabled={running}
                  onChoose={(fmt) => {
                    if (fmt === "html") void runHtmlExport();
                    else void runExport(fmt);
                  }}
                />
                {running ? (
                  <p className={styles.formatBusy} aria-live="polite">
                    Working on your export…
                  </p>
                ) : null}
              </div>
            </>
          ) : null}
        </div>

        {error ? <p className={styles.error}>{error}</p> : null}

        <div className={styles.actions}>
          {step > 1 ? (
            <Button
              size="Small"
              colour="#FFF"
              textColour="#000"
              border="rgba(0,0,0,0.2)"
              disabled={running}
              clickHandler={() =>
                setStep((s) => (s === 3 ? 2 : 1) as 1 | 2 | 3)
              }
            >
              Back
            </Button>
          ) : null}
          {step < 3 ? (
            <Button
              size="Small"
              colour="rgb(36, 57, 79)"
              textColour="#FFF"
              border="transparent"
              disabled={
                running ||
                (step === 1 && !canNextStep1) ||
                (step === 2 && !canNextStep2)
              }
              clickHandler={() => {
                setError(null);
                setStep((s) => (s === 1 ? 2 : 3));
              }}
            >
              Next
            </Button>
          ) : null}
          <Button
            size="Small"
            colour="#FFF"
            textColour="#000"
            border="rgba(0,0,0,0.2)"
            disabled={running}
            clickHandler={onClose}
          >
            Cancel
          </Button>
        </div>
      </div>
    </Popup>
  );
};

export default BulkExportWizard;
